import React, { useEffect, useState } from 'react';
import { Button, Card, Typography, Row, Col, Divider, Space, Badge } from 'antd';
import { useNavigate } from 'react-router-dom';
import {
  RocketOutlined,
  SafetyCertificateOutlined,
  ApiOutlined,
  DashboardOutlined,
  BarChartOutlined,
  SettingOutlined,
  ThunderboltOutlined,
  GlobalOutlined,
  SecurityScanOutlined,
  DatabaseOutlined,
  CloudServerOutlined,
  BulbOutlined,
  StarOutlined,
  CheckCircleOutlined,
  ArrowRightOutlined,
  PlayCircleOutlined,
  SunOutlined,
  MoonOutlined,
} from '@ant-design/icons';
import './LandingPage.css';

const { Title, Paragraph, Text, Link } = Typography;

const features = [
  {
    icon: <SafetyCertificateOutlined />,
    title: '用户与权限管理',
    desc: '基于RBAC的多层级权限体系，支持SSO、MFA双因子认证，确保企业级安全标准。',
    color: '#1677ff',
  },
  {
    icon: <ApiOutlined />,
    title: 'API密钥管理',
    desc: '智能密钥生命周期管理，支持密钥轮换、权限分级、调用链路追踪和异常监控。',
    color: '#52c41a',
  },
  {
    icon: <RocketOutlined />,
    title: 'AI大模型集成',
    desc: '深度集成全球顶尖AI模型，提供智能负载均衡、故障自动切换、成本优化路由策略。',
    color: '#722ed1',
  },
  {
    icon: <DashboardOutlined />,
    title: '计费与余额系统',
    desc: '精确到Token级别的实时计费，支持预算控制、成本预警、多维度成本分析。',
    color: '#fa8c16',
  },
  {
    icon: <BarChartOutlined />,
    title: '日志与统计分析',
    desc: '分布式日志系统，支持全链路追踪、性能监控、异常告警和业务洞察分析。',
    color: '#13c2c2',
  },
  {
    icon: <SettingOutlined />,
    title: '偏好设置与个性化',
    desc: '基于AI的个性化推荐，智能参数调优建议，自适应用户界面和工作流优化。',
    color: '#eb2f96',
  },
];

const highlights = [
  { icon: <SecurityScanOutlined />, text: '零信任安全架构，端到端加密传输，符合SOC2/ISO27001标准' },
  { icon: <ThunderboltOutlined />, text: '毫秒级响应时间，支持10万+并发，99.99%高可用保障' },
  { icon: <GlobalOutlined />, text: '智能模型路由引擎，自动故障切换，全球节点就近访问' },
  { icon: <DatabaseOutlined />, text: '多层防护体系：API网关、限流熔断、DDoS防护、审计日志' },
  { icon: <CloudServerOutlined />, text: 'GitOps自动化运维，蓝绿部署，灰度发布，一键回滚' },
  { icon: <BulbOutlined />, text: 'GraphQL统一数据层，RESTful API，SDK多语言支持' },
];

const scenarios = [
  { title: '企业AI中台建设', icon: '🏢', desc: '构建统一AI服务治理平台，赋能全组织AI创新' },
  { title: 'SaaS产品AI增强', icon: '🔗', desc: '为现有产品快速集成AI能力，提升竞争力' },
  { title: '智能应用开发', icon: '🤖', desc: '低代码AI应用构建，加速产品迭代' },
  { title: '业务流程智能化', icon: '⚡', desc: '传统业务系统AI化改造，提效降本' },
];

const stats = [
  { label: 'AI模型支持', value: '5+', suffix: '个' },
  { label: 'API调用', value: '99.9%', suffix: '可用性' },
  { label: '响应时间', value: '<100', suffix: 'ms' },
  { label: '并发支持', value: '10K+', suffix: 'QPS' },
];

const faqs = [
  {
    q: '支持哪些AI模型和服务商？',
    a: '深度集成OpenAI GPT系列、Google Gemini、Anthropic Claude、DeepSeek、xAI Grok等顶级模型，支持自定义模型接入。',
  },
  {
    q: '如何保证企业级安全？',
    a: '零信任安全架构、端到端加密、多因子认证、RBAC权限体系、API限流防护、全链路审计日志。',
  },
  {
    q: '成本控制和计费管理如何实现？',
    a: '精确到Token级的实时计费、预算告警、成本归因分析、多维度报表、支持企业采购和财务对账。',
  },
  {
    q: '支持私有云和混合云部署吗？',
    a: '支持公有云SaaS、私有云部署、混合云架构，提供从POC到生产的全生命周期服务。',
  },
];

const aiModels = [
  { name: 'DeepSeek', status: 'active' },
  { name: 'OpenAI', status: 'active' },
  { name: 'Gemini', status: 'active' },
  { name: 'Anthropic', status: 'active' },
  { name: 'Grok', status: 'active' },
  { name: 'Claude', status: 'coming' },
];

const LandingPage: React.FC = () => {
  const navigate = useNavigate();
  const [currentModelIndex, setCurrentModelIndex] = useState(0);
  const [theme, setTheme] = useState<'dark' | 'light'>('dark');

  const toggleTheme = () => {
    setTheme(prev => prev === 'dark' ? 'light' : 'dark');
  };

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentModelIndex((prev) => (prev + 1) % aiModels.length);
    }, 2000);
    return () => clearInterval(interval);
  }, []);

  return (
    <div className={`landing-page ${theme === 'dark' ? '' : 'light-mode'}`}>
      {/* 动态背景 */}
      <div className="background-animation">
        <div className="floating-particles"></div>
        <div className="grid-pattern"></div>
      </div>

      {/* 顶部导航栏 */}
      <header className="landing-header">
        <div className="container">
          <div className="nav-brand">
            <div className="logo-container">
              <ApiOutlined className="logo-icon" />
              <Title level={2} className="brand-title">APILink</Title>
              <Badge count="Pro" className="version-badge" />
            </div>
          </div>
          <nav className="nav-actions">
            <Button
              type="text"
              icon={theme === 'dark' ? <SunOutlined /> : <MoonOutlined />}
              onClick={toggleTheme}
              className="theme-toggle-btn"
            />
            <Button type="text" size="large" className="nav-button" onClick={() => navigate('/login')}>
              登录
            </Button>
            <Button type="primary" size="large" className="cta-button" onClick={() => navigate('/register')}>
              免费开始
            </Button>
          </nav>
        </div>
      </header>

      {/* 主横幅区域 */}
      <section className="hero-section">
        <div className="container">
          <div className="hero-content">
            <div className="hero-left">
              <div className="hero-badge">
                <Badge count="New" />
                <span>下一代AI基础设施平台</span>
              </div>
              <Title className="hero-title">
                构建企业级
                <span className="gradient-text">AI基础设施</span>
                统一平台
              </Title>
              <Paragraph className="hero-subtitle">
                集成全球顶尖AI模型的企业级API管理平台。提供统一接口、智能路由、
                细粒度权限控制和实时监控，让您的AI应用开发更加高效、安全、可控。
              </Paragraph>
              <div className="hero-stats">
                {stats.map((stat, index) => (
                  <div key={index} className="stat-item">
                    <div className="stat-value">{stat.value}</div>
                    <div className="stat-label">{stat.label}</div>
                  </div>
                ))}
              </div>
              <div className="hero-actions">
                <Button
                  type="primary"
                  size="large"
                  className="primary-cta"
                  icon={<RocketOutlined />}
                  onClick={() => navigate('/register')}
                >
                  立即开始
                </Button>
                <Button
                  size="large"
                  className="secondary-cta"
                  icon={<PlayCircleOutlined />}
                  onClick={() => navigate('/login')}
                >
                  查看演示
                </Button>
              </div>
            </div>
            <div className="hero-right">
              <div className="hero-visual">
                <div className="terminal-window">
                  <div className="terminal-header">
                    <div className="terminal-buttons">
                      <span className="btn red"></span>
                      <span className="btn yellow"></span>
                      <span className="btn green"></span>
                    </div>
                    <div className="terminal-title">APILink Console</div>
                  </div>
                  <div className="terminal-content">
                    <div className="code-line">
                      <span className="prompt">$ </span>
                      <span className="command">curl -X POST https://api.apilink.com/v1/chat/completions</span>
                    </div>
                    <div className="code-line">
                      <span className="comment"># AI Model: {aiModels[currentModelIndex]?.name}</span>
                    </div>
                    <div className="code-line">
                      <span className="response">✓ Response: 200 OK (87ms)</span>
                    </div>
                  </div>
                </div>
                <div className="floating-cards">
                  {aiModels.slice(0, 3).map((model, index) => (
                    <div key={index} className={`floating-card card-${index + 1}`}>
                      <div className="card-icon">🤖</div>
                      <div className="card-title">{model.name}</div>
                      <div className={`status-indicator ${model.status}`}></div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* AI模型展示区 */}
      <section className="models-section">
        <div className="container">
          <div className="section-header">
            <Badge count="HOT" className="section-badge" />
            <Title level={2} className="section-title">全球顶尖AI模型生态</Title>
            <Paragraph className="section-subtitle">
              无缝集成OpenAI、Google、Anthropic等顶级AI模型，统一接口标准，一次接入全部可用
            </Paragraph>
          </div>
          <div className="models-grid">
            {aiModels.map((model, index) => (
              <div key={index} className={`model-card ${model.status} ${index === currentModelIndex ? 'active' : ''}`}>
                <div className="model-icon">🤖</div>
                <div className="model-name">{model.name}</div>
                <div className={`model-status ${model.status}`}>
                  {model.status === 'active' ? '已接入' : '即将上线'}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 核心功能区域 */}
      <section className="features-section">
        <div className="container">
          <div className="section-header">
            <Title level={2} className="section-title">企业级核心能力</Title>
            <Paragraph className="section-subtitle">
              构建完整AI基础设施生态，从接入管理到智能调度，全方位赋能企业AI转型
            </Paragraph>
          </div>
          <Row gutter={[32, 32]} className="features-grid">
            {features.map((feature, index) => (
              <Col xs={24} sm={12} lg={8} key={index}>
                <Card className="feature-card" hoverable>
                  <div className="feature-icon" style={{ color: feature.color }}>
                    {feature.icon}
                  </div>
                  <Title level={4} className="feature-title">{feature.title}</Title>
                  <Paragraph className="feature-desc">{feature.desc}</Paragraph>
                  <div className="feature-link">
                    了解更多 <ArrowRightOutlined />
                  </div>
                </Card>
              </Col>
            ))}
          </Row>
        </div>
      </section>

      {/* 技术亮点区域 */}
      <section className="highlights-section">
        <div className="container">
          <div className="highlights-content">
            <div className="highlights-left">
              <Title level={2} className="section-title">核心技术优势</Title>
              <Paragraph className="section-subtitle">
                云原生微服务架构，支持弹性扩容、多云部署、边缘计算，构建面向未来的AI基础设施
              </Paragraph>
              <div className="highlights-list">
                {highlights.map((highlight, index) => (
                  <div key={index} className="highlight-item">
                    <div className="highlight-icon">{highlight.icon}</div>
                    <span className="highlight-text">{highlight.text}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="highlights-right">
              <div className="architecture-diagram">
                <div className="arch-layer frontend">
                  <div className="layer-title">前端层</div>
                  <div className="layer-tech">React + TypeScript</div>
                </div>
                <div className="arch-layer api">
                  <div className="layer-title">API网关</div>
                  <div className="layer-tech">Go + Gin</div>
                </div>
                <div className="arch-layer service">
                  <div className="layer-title">服务层</div>
                  <div className="layer-tech">微服务架构</div>
                </div>
                <div className="arch-layer data">
                  <div className="layer-title">数据层</div>
                  <div className="layer-tech">MySQL + Redis</div>
                </div>
                <div className="data-flow"></div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 应用场景区域 */}
      <section className="scenarios-section">
        <div className="container">
          <div className="section-header">
            <Title level={2} className="section-title">企业应用场景</Title>
            <Paragraph className="section-subtitle">
              从初创公司到大型企业，从单一应用到复杂AI生态，全面覆盖各种业务场景
            </Paragraph>
          </div>
          <Row gutter={[24, 24]} className="scenarios-grid">
            {scenarios.map((scenario, index) => (
              <Col xs={24} sm={12} lg={6} key={index}>
                <Card className="scenario-card" hoverable>
                  <div className="scenario-icon">{scenario.icon}</div>
                  <Title level={5} className="scenario-title">{scenario.title}</Title>
                  <Paragraph className="scenario-desc">{scenario.desc}</Paragraph>
                </Card>
              </Col>
            ))}
          </Row>
        </div>
      </section>

      {/* FAQ区域 */}
      <section className="faq-section">
        <div className="container">
          <div className="section-header">
            <Title level={2} className="section-title">常见问题解答</Title>
            <Paragraph className="section-subtitle">
              专业团队为您解答技术架构、安全合规、部署运维等关键问题
            </Paragraph>
          </div>
          <div className="faq-grid">
            {faqs.map((faq, index) => (
              <Card key={index} className="faq-card" hoverable>
                <div className="faq-question">
                  <CheckCircleOutlined className="faq-icon" />
                  <Text strong className="faq-q">{faq.q}</Text>
                </div>
                <Paragraph className="faq-answer">{faq.a}</Paragraph>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA区域 */}
      <section className="cta-section">
        <div className="container">
          <div className="cta-content">
            <Title level={2} className="cta-title">
              开启您的AI基础设施建设之旅
            </Title>
            <Paragraph className="cta-subtitle">
              加入数千家企业的选择，构建面向未来的AI驱动业务平台，释放人工智能的无限潜能
            </Paragraph>
            <div className="cta-actions">
              <Button
                type="primary"
                size="large"
                className="cta-primary"
                icon={<RocketOutlined />}
                onClick={() => navigate('/register')}
              >
                免费开始使用
              </Button>
              <Button
                size="large"
                className="cta-secondary"
                onClick={() => navigate('/login')}
              >
                已有账号？立即登录
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* 页脚 */}
      <footer className="landing-footer">
        <div className="container">
          <div className="footer-content">
            <div className="footer-brand">
              <div className="footer-logo">
                <ApiOutlined />
                <span>APILink</span>
              </div>
              <Paragraph>构建面向未来的AI基础设施</Paragraph>
            </div>
            <div className="footer-links">
              <div className="link-group">
                <Title level={5}>产品</Title>
                <Link>功能特性</Link>
                <Link>定价方案</Link>
                <Link>API文档</Link>
              </div>
              <div className="link-group">
                <Title level={5}>支持</Title>
                <Link>帮助中心</Link>
                <Link href="mailto:support@apilink.com">技术支持</Link>
                <Link>开发者社区</Link>
              </div>
              <div className="link-group">
                <Title level={5}>公司</Title>
                <Link>关于我们</Link>
                <Link>联系我们</Link>
                <Link>隐私政策</Link>
              </div>
            </div>
          </div>
          <Divider />
          <div className="footer-bottom">
            <Text type="secondary">
              © 2024 APILink. 保留所有权利。
            </Text>
            <div className="footer-social">
              <Text type="secondary">关注我们：</Text>
              <Space>
                <Link>微信</Link>
                <Link>GitHub</Link>
                <Link>文档</Link>
              </Space>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;